import Head from 'next/head'
import { useMemo } from 'react'
import { FB_APP_ID, toTitleCase } from '../../lib/Utils'
import useFetchUrl from '../../hooks/useFetchUrl'
import { jsonLdScriptProps } from 'react-schemaorg'
import { JobPosting, Place } from 'schema-dts'
import ICompany from '../../types/Company'

interface Props {
  title: string
  details: string
  imageUrl: string
  overview: string
  client: ICompany
  hiringArea: any
}

const today = new Date()

const JobPostDetailHead = (props: Props) => {
  const { currentUrl, domain } = useFetchUrl()

  const title = useMemo(() => toTitleCase(props.title), [props.title])

  const description = useMemo(
    () => props.overview?.replace(/<.*?>/g, '').substring(0, 255) + '...',
    [props.overview]
  )

  const locations: Place[] = useMemo(() => {
    const hiringArea = props.hiringArea
    const locations: Place[] = []

    if (!hiringArea.rules) {
      return []
    }
    //add states to structured-data schema
    if (hiringArea.rules.in?.states?.length > 0) {
      locations.push({
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressRegion: hiringArea.rules.in?.states,
          addressCountry: 'United States'
        }
      })
    }

    // add cities to structured-data schema
    if (hiringArea.rules.in?.cities?.length > 0) {
      for (const el of hiringArea.rules.in?.cities) {
        if (!el.city) {
          continue
        }
        // console.log(el.city);
        locations.push({
          '@type': 'Place',
          geo: {
            '@type': 'GeoCoordinates',
            latitude: el.city.geo.coordinates[1],
            longitude: el.city.geo.coordinates[0]
          },
          address: {
            '@type': 'PostalAddress',
            addressLocality: el.city.name,
            addressRegion: el.city.stateCode,
            streetAddress: el.city.name + ', ' + el.city.stateCode,
            addressCountry: 'United States'
          }
        })
      }
    }

    // add zipcodes to structured-data schema
    if (hiringArea.rules.in?.zipCodes?.length > 0) {
      for (const el of hiringArea.rules.in?.zipCodes) {
        if (!el.geo) {
          continue
        }
        locations.push({
          '@type': 'Place',
          geo: {
            '@type': 'GeoCoordinates',
            latitude: el.geo.coordinates[1],
            longitude: el.geo.coordinates[0]
          },
          address: {
            '@type': 'PostalAddress',
            postalCode: el.zipCode,
            addressCountry: 'United States'
          }
        })
      }
    }
    return locations
  }, [props.hiringArea])

  return (
    <Head>
      <title>{title}</title>

      <meta name="twitter:card" content="summary" key="twitterCard"></meta>
      <meta property="og:url" content={currentUrl} key="ogUrl"></meta>
      <meta property="og:title" content={title} key="ogTitle" />

      <meta name="description" content={description} key="description"></meta>
      <meta
        property="og:description"
        content={description}
        key="ogDescription"></meta>
      <meta
        property="og:image"
        content={props.imageUrl || `${domain}/assets/img/company/default.webp`}
        key="ogImage"></meta>

      <meta property="og:type" content="website" key="ogType"></meta>
      <meta property="fb:app_id" content={FB_APP_ID} key="fbAppId"></meta>

      <script
        {...jsonLdScriptProps<JobPosting>({
          '@context': 'https://schema.org',
          '@type': 'JobPosting',
          url: currentUrl,
          title: title,
          description: props.details,

          datePosted: new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDay() - Math.round(7 * Math.random())
          )
            .toISOString()
            .split('T')[0],
          employmentType: 'FULL_TIME',
          hiringOrganization: {
            '@type': 'Organization',
            name: props.client.name,
            logo: props.client.resources?.logo?.Location
          },
          jobLocation: locations,
          validThrough: new Date(today.getFullYear(), today.getMonth() + 1, 1)
            .toISOString()
            .split('T')[0],
          occupationalCategory: 'Transportation and Logistics'
        })}
      />
    </Head>
  )
}

export default JobPostDetailHead
